import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Campaign, State } from '../shared/model';
import {
  brlToCents,
  promotionStatuses,
  type PromotionPlan,
  type PromotionReading,
  type PromotionStatus,
  type PromotionWorkspaceData,
} from '../shared/promotions';
import './promotions.css';

const labels: Record<PromotionStatus, string> = {
  planejado: 'Planejado',
  em_veiculacao: 'Veiculação informada',
  pausado: 'Pausa informada',
  encerrado: 'Encerrado',
  cancelado: 'Cancelado',
};
const money = (value: number | null) =>
  value === null
    ? 'Não informado'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);
const metric = (value: number | null) =>
  value === null ? 'Não informado' : value.toLocaleString('pt-BR');
const date = (value: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value).toLocaleDateString(
    'pt-BR',
    {
      timeZone: 'America/Sao_Paulo',
    },
  );
const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const field = (form: FormData, key: string) => String(form.get(key) ?? '').trim();
const integer = (form: FormData, key: string) => {
  const value = field(form, key);
  if (!value) return null;
  const number = Number(value);
  if (!/^\d+$/.test(value) || !Number.isSafeInteger(number))
    throw new Error('Use contagens inteiras, sem separador de milhar.');
  return number;
};
const amountInput = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : (value / 100).toFixed(2);
function link(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : undefined;
  } catch {
    return undefined;
  }
}

type Props = { campaign: Campaign; data: State };
type Editor =
  | { kind: 'plan'; item?: PromotionPlan }
  | { kind: 'reading'; plan: PromotionPlan; item?: PromotionReading };

export function PromotionWorkspace({ campaign, data }: Props) {
  const [workspace, setWorkspace] = useState<PromotionWorkspaceData>();
  const [editor, setEditor] = useState<Editor>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const sequence = useRef(0);
  const canEdit = data.permissions.edit;
  const titleFor = (id: string) => {
    const content = data.contents.find((item) => item.id === id);
    return content
      ? `${content.flgId ? `${content.flgId} · ` : ''}${content.title}`
      : 'Publicação registrada';
  };
  async function load() {
    const current = ++sequence.current;
    const response = await fetch(`/api/campaigns/${encodeURIComponent(campaign.id)}/promotions`);
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível carregar os testes.');
    if (current === sequence.current) setWorkspace(result);
  }
  useEffect(() => {
    setWorkspace(undefined);
    setEditor(undefined);
    setError('');
    setNotice('');
    void load().catch((reason) => setError(reason.message));
    return () => {
      sequence.current++;
    };
  }, [campaign.id]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !editor) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      let path: string, method: string, body: unknown;
      if (editor.kind === 'plan') {
        const budgetCents = brlToCents(field(form, 'budget'));
        if (budgetCents === null || budgetCents <= 0)
          throw new Error('Informe o orçamento planejado em reais.');
        path = editor.item
          ? `/api/promotions/${encodeURIComponent(editor.item.id)}`
          : '/api/promotions';
        method = editor.item ? 'PUT' : 'POST';
        body = {
          campaignId: campaign.id,
          publicationId: editor.item?.publicationId || field(form, 'publicationId'),
          name: field(form, 'name'),
          objective: field(form, 'objective'),
          budgetCents,
          startDate: field(form, 'startDate'),
          endDate: field(form, 'endDate'),
          status: editor.item ? field(form, 'status') : 'planejado',
          externalReference: field(form, 'externalReference'),
          evidence: field(form, 'evidence'),
          ...(editor.item ? { version: editor.item.version } : {}),
        };
      } else {
        path = `/api/promotions/${encodeURIComponent(editor.plan.id)}/readings`;
        method = 'POST';
        body = {
          planVersion: editor.plan.version,
          replacesId: editor.item?.id ?? null,
          source: field(form, 'source'),
          evidence: field(form, 'evidence'),
          externalReference: field(form, 'externalReference'),
          scope: field(form, 'scope'),
          periodStart: field(form, 'periodStart'),
          periodEnd: field(form, 'periodEnd'),
          spentCents: brlToCents(field(form, 'spent')),
          profileVisits: integer(form, 'profileVisits'),
          followers: integer(form, 'followers'),
          interactions: integer(form, 'interactions'),
          videoViews: integer(form, 'videoViews'),
        };
      }
      const response = await fetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409) await load();
        throw new Error(result.error || 'Não foi possível salvar o registro.');
      }
      setEditor(undefined);
      await load();
      setNotice('Registro salvo no histórico do teste.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  }
  function open(next: Editor) {
    setEditor(next);
    setError('');
    setNotice('');
  }
  return (
    <section className="promotion-workspace" aria-label="Testes de impulsionamento">
      <header className="promotion-heading">
        <div>
          <h2>Testes de impulsionamento</h2>
          <p>Planeje cada teste e registre os resultados informados pela plataforma.</p>
        </div>
        {canEdit && (
          <button
            className="primary"
            disabled={!workspace?.publications.length || busy}
            onClick={() => open({ kind: 'plan' })}
          >
            Planejar teste
          </button>
        )}
      </header>
      <p className="promotion-notice">
        Este acompanhamento é manual. Salvar um plano não cria anúncios, não ativa a Meta e não
        autoriza gastos.
      </p>
      {error && (
        <p className="promotion-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="promotion-success" role="status">
          {notice}
        </p>
      )}
      {!workspace && !error && <p role="status">Carregando testes…</p>}
      {editor && (
        <form
          className="promotion-form"
          onSubmit={submit}
          key={
            editor.kind === 'reading'
              ? `reading:${editor.plan.id}:${editor.item?.id ?? 'new'}`
              : `plan:${editor.item?.id ?? 'new'}`
          }
        >
          <h3>
            {editor.kind === 'plan'
              ? editor.item
                ? 'Revisar teste'
                : 'Planejar novo teste'
              : editor.item
                ? 'Corrigir leitura com nova revisão'
                : 'Registrar leitura manual'}
          </h3>
          {editor.kind === 'reading' && (
            <p>
              Teste: <strong>{editor.plan.name}</strong>
            </p>
          )}
          {editor.kind === 'plan' ? (
            <div className="promotion-fields">
              <label className="promotion-wide">
                Publicação real
                <select
                  name="publicationId"
                  required
                  defaultValue={editor.item?.publicationId ?? ''}
                  disabled={Boolean(editor.item)}
                >
                  <option value="">Selecione uma publicação</option>
                  {workspace?.publications.map((item) => (
                    <option key={item.id} value={item.id}>
                      {titleFor(item.contentId)} · {item.account} · {date(item.publishedAt)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Nome do teste
                <input
                  name="name"
                  required
                  maxLength={250}
                  defaultValue={editor.item?.name ?? ''}
                />
              </label>
              <label>
                Orçamento planejado (R$)
                <input
                  name="budget"
                  inputMode="decimal"
                  required
                  placeholder="Informe o valor"
                  defaultValue={amountInput(editor.item?.budgetCents)}
                />
              </label>
              <label>
                Início do período
                <input
                  name="startDate"
                  type="date"
                  required
                  defaultValue={editor.item?.startDate ?? ''}
                />
              </label>
              <label>
                Fim do período
                <input
                  name="endDate"
                  type="date"
                  required
                  defaultValue={editor.item?.endDate ?? ''}
                />
              </label>
              <label className="promotion-wide">
                Objetivo
                <textarea
                  name="objective"
                  required
                  minLength={3}
                  defaultValue={editor.item?.objective ?? ''}
                />
              </label>
              {editor.item && (
                <label>
                  Situação registrada
                  <select name="status" defaultValue={editor.item.status}>
                    {promotionStatuses.map((status) => (
                      <option key={status} value={status}>
                        {labels[status]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="promotion-wide">
                URL ou ID externo da veiculação
                <input
                  name="externalReference"
                  maxLength={3000}
                  defaultValue={editor.item?.externalReference ?? ''}
                />
                <small>
                  Obrigatório para registrar veiculação. O FLG não verifica nem modifica o anúncio.
                </small>
              </label>
              <label className="promotion-wide">
                Evidência ou motivo da revisão
                <textarea
                  name="evidence"
                  required={Boolean(editor.item)}
                  minLength={editor.item ? 3 : undefined}
                  defaultValue={editor.item?.evidence ?? ''}
                />
              </label>
            </div>
          ) : (
            <div className="promotion-fields">
              <p className="promotion-wide promotion-notice">
                Informe a leitura da fonte para o período inteiro selecionado. Deixe campos sem
                informação em branco; zero significa que a fonte informou zero. Leituras acumuladas
                não são somadas.
              </p>
              <label className="promotion-wide">
                Tipo de resultado na fonte
                <select name="scope" required defaultValue={editor.item?.scope ?? ''}>
                  <option value="">Selecione o que a fonte identifica</option>
                  <option value="pago_declarado">Resultado pago identificado na fonte</option>
                  <option value="nao_separado">Total sem separar pago e orgânico</option>
                </select>
              </label>
              <label>
                Início da leitura
                <input
                  name="periodStart"
                  type="date"
                  required
                  min={editor.plan.startDate}
                  max={editor.plan.endDate}
                  defaultValue={editor.item?.periodStart ?? editor.plan.startDate}
                />
              </label>
              <label>
                Fim da leitura
                <input
                  name="periodEnd"
                  type="date"
                  required
                  min={editor.plan.startDate}
                  max={editor.plan.endDate < data.today ? editor.plan.endDate : data.today}
                  defaultValue={editor.item?.periodEnd ?? ''}
                />
              </label>
              <label>
                Gasto informado (R$)
                <input
                  name="spent"
                  inputMode="decimal"
                  defaultValue={amountInput(editor.item?.spentCents)}
                />
              </label>
              {(
                [
                  ['profileVisits', 'Visitas ao perfil'],
                  ['followers', 'Seguidores atribuídos pela fonte'],
                  ['interactions', 'Interações'],
                  ['videoViews', 'Visualizações do vídeo'],
                ] as const
              ).map(([key, label]) => (
                <label key={key}>
                  {label}
                  <input
                    name={key}
                    type="number"
                    min="0"
                    step="1"
                    defaultValue={editor.item?.[key] ?? ''}
                  />
                </label>
              ))}
              <label className="promotion-wide">
                Fonte da leitura
                <input
                  name="source"
                  required
                  minLength={3}
                  maxLength={250}
                  placeholder="Ex.: relatório do impulsionamento no Instagram"
                  defaultValue={editor.item?.source ?? ''}
                />
              </label>
              <label className="promotion-wide">
                URL ou ID externo
                <input
                  name="externalReference"
                  required
                  maxLength={3000}
                  defaultValue={editor.item?.externalReference ?? editor.plan.externalReference}
                />
              </label>
              <label className="promotion-wide">
                Evidência e contexto
                <textarea
                  name="evidence"
                  required
                  minLength={3}
                  placeholder="Onde os números foram conferidos e eventuais limites de atribuição"
                  defaultValue={editor.item?.evidence ?? ''}
                />
              </label>
            </div>
          )}
          <div className="promotion-actions">
            <button className="primary" disabled={busy}>
              {busy ? 'Salvando…' : 'Salvar registro'}
            </button>
            <button type="button" disabled={busy} onClick={() => setEditor(undefined)}>
              Cancelar edição
            </button>
          </div>
        </form>
      )}
      {workspace && !workspace.publications.length && (
        <p className="promotion-empty">
          Vincule uma publicação real a esta campanha para planejar seu primeiro teste.
        </p>
      )}
      {workspace?.publications.length && !workspace.tests.length ? (
        <p className="promotion-empty">
          Nenhum teste registrado. O orçamento será informado por você ao planejar.
        </p>
      ) : null}
      <div className="promotion-cards">
        {workspace?.tests.map((test) => (
          <article className="promotion-card" key={test.plan.id}>
            <header>
              <div>
                <span className={`promotion-status status-${test.plan.status}`}>
                  {labels[test.plan.status]}
                </span>
                <h3>{test.plan.name}</h3>
                <p>
                  {titleFor(test.plan.contentId)} · {test.plan.account}
                </p>
              </div>
              {link(test.plan.publicationUrl) && (
                <a href={link(test.plan.publicationUrl)} target="_blank" rel="noreferrer">
                  Ver publicação ↗
                </a>
              )}
            </header>
            <p>{test.plan.objective}</p>
            <dl className="promotion-summary">
              <div>
                <dt>Orçamento planejado</dt>
                <dd>{money(test.plan.budgetCents)}</dd>
              </div>
              <div>
                <dt>Período do teste</dt>
                <dd>
                  {date(test.plan.startDate)} a {date(test.plan.endDate)}
                </dd>
              </div>
            </dl>
            {test.plan.externalReference && (
              <p className="promotion-reference">
                Referência externa: {test.plan.externalReference}
              </p>
            )}
            <section className="promotion-reading">
              <h4>Última leitura manual</h4>
              {test.latest ? (
                <>
                  <p>
                    <strong>
                      {test.latest.scope === 'pago_declarado'
                        ? 'Pago identificado na fonte'
                        : 'Total sem separar pago e orgânico'}
                    </strong>{' '}
                    · {date(test.latest.periodStart)} a {date(test.latest.periodEnd)}
                  </p>
                  <dl className="promotion-summary">
                    <div>
                      <dt>Gasto informado</dt>
                      <dd>{money(test.latest.spentCents)}</dd>
                    </div>
                    <div>
                      <dt>Visitas ao perfil</dt>
                      <dd>{metric(test.latest.profileVisits)}</dd>
                    </div>
                    <div>
                      <dt>Custo por visita</dt>
                      <dd>
                        {test.costPerVisitCents === null
                          ? 'Não calculável'
                          : money(test.costPerVisitCents)}
                      </dd>
                    </div>
                    <div>
                      <dt>Seguidores atribuídos pela fonte</dt>
                      <dd>{metric(test.latest.followers)}</dd>
                    </div>
                    <div>
                      <dt>Interações</dt>
                      <dd>{metric(test.latest.interactions)}</dd>
                    </div>
                    <div>
                      <dt>Visualizações do vídeo</dt>
                      <dd>{metric(test.latest.videoViews)}</dd>
                    </div>
                  </dl>
                  <p>
                    Fonte: {test.latest.source} · registrada em {stamp(test.latest.createdAt)}
                  </p>
                  <p>{test.latest.evidence}</p>
                </>
              ) : (
                <p>Nenhum resultado informado. O orçamento planejado não é gasto realizado.</p>
              )}
            </section>
            <details className="promotion-history">
              <summary>Leitura Meta de referência</summary>
              {test.plan.baseline ? (
                <>
                  <p>
                    Leitura total/lifetime coletada em {stamp(test.plan.baseline.collectedAt)},
                    preservada ao criar o teste. Não identifica resultados orgânicos nem prova
                    ausência de impulsionamentos anteriores. Nenhuma subtração é feita para estimar
                    resultados pagos.
                  </p>
                  {test.baseline ? (
                    <dl className="promotion-summary">
                      {test.baseline.metrics.map((item) => (
                        <div key={item.key}>
                          <dt>{item.label}</dt>
                          <dd>
                            {item.status === 'available' && item.value !== null
                              ? `${metric(item.value)}${item.unit === 'milliseconds' ? ' ms' : ''}`
                              : 'Não disponível'}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p>
                      A leitura de referência não está disponível na conexão atual. Os valores não
                      são mantidos em cópias no plano.
                    </p>
                  )}
                </>
              ) : (
                <p>
                  Nenhuma leitura Meta estava disponível ao criar o teste. Isso não significa
                  resultado zero.
                </p>
              )}
            </details>
            {test.readings.length > 0 && (
              <details className="promotion-history">
                <summary>Histórico de leituras ({test.readings.length})</summary>
                <p>
                  O resumo usa somente a última leitura registrada. Períodos acumulados ou
                  sobrepostos não são somados.
                </p>
                {test.readings.map((reading) => (
                  <div className="promotion-history-item" key={reading.id}>
                    <strong>
                      Leitura {reading.revision} · {date(reading.periodStart)} a{' '}
                      {date(reading.periodEnd)}
                    </strong>
                    <p>
                      {reading.scope === 'pago_declarado'
                        ? 'Pago identificado na fonte'
                        : 'Total sem separação'}{' '}
                      · gasto {money(reading.spentCents)} · visitas {metric(reading.profileVisits)}{' '}
                      · {reading.source}
                    </p>
                    <p>{reading.evidence}</p>
                    <details>
                      <summary>Detalhes desta leitura · plano versão {reading.planVersion}</summary>
                      <dl className="promotion-summary">
                        <div>
                          <dt>Seguidores atribuídos pela fonte</dt>
                          <dd>{metric(reading.followers)}</dd>
                        </div>
                        <div>
                          <dt>Interações</dt>
                          <dd>{metric(reading.interactions)}</dd>
                        </div>
                        <div>
                          <dt>Visualizações do vídeo</dt>
                          <dd>{metric(reading.videoViews)}</dd>
                        </div>
                      </dl>
                      <p>Referência externa: {reading.externalReference}</p>
                      <p>
                        Registrada em {stamp(reading.createdAt)} · versão {reading.revision} da
                        leitura.
                      </p>
                    </details>
                    {reading.replacesId && (
                      <small>Correção de uma leitura anterior; original preservada.</small>
                    )}
                  </div>
                ))}
              </details>
            )}
            <details className="promotion-history">
              <summary>Revisões do plano ({test.revisions.length})</summary>
              {test.revisions.map((revision) => (
                <div className="promotion-history-item" key={revision.id}>
                  <strong>
                    Versão {revision.planVersion} · {labels[revision.plan.status]}
                  </strong>
                  <p>
                    {money(revision.plan.budgetCents)} · {date(revision.plan.startDate)} a{' '}
                    {date(revision.plan.endDate)} · {stamp(revision.createdAt)}
                  </p>
                  <p>{revision.plan.evidence || 'Planejamento inicial.'}</p>
                </div>
              ))}
            </details>
            {canEdit && (
              <div className="promotion-actions">
                <button disabled={busy} onClick={() => open({ kind: 'plan', item: test.plan })}>
                  Revisar plano
                </button>
                <button disabled={busy} onClick={() => open({ kind: 'reading', plan: test.plan })}>
                  Registrar leitura
                </button>
                {test.latest && (
                  <button
                    disabled={busy}
                    onClick={() => open({ kind: 'reading', plan: test.plan, item: test.latest! })}
                  >
                    Corrigir última leitura
                  </button>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
